// Suivre un gros site demande de choisir ce qu'on y veut (issue #191,
// src/services/filtreAbonnement.ts).
//
// Tous les boutons « Suivre » passent par `useSuivreSite` : un petit site se
// suit d'un clic, et le serveur répond 422 `filtre_requis` pour un gros. La
// fenêtre s'ouvre alors, préremplie avec les Réglages (GET …/filtre), et le
// suivi repart avec le filtre choisi. Un bouton qui appellerait la route
// lui-même laisserait le refus s'afficher en notification d'erreur.

import { Check, Filter } from 'lucide-react'
import { useRef, useState } from 'react'
import { ApiError, get, post } from '../lib/api'
import { useT, type CleUi } from '../lib/i18n'
import { Modal, useToast } from './common'
import { SOUS_ZONES, type FiltreAbonnement, type LangueAnnonce as Langue, type Zone } from '../../services/filtreOffre'

export type { FiltreAbonnement }

interface OptionsFiltre {
  gros: boolean
  total: number
  filtre: FiltreAbonnement | null
  proposition: FiltreAbonnement | null
  zones: { code: Zone; region: string; n: number }[]
  langues: { code: Langue; n: number }[]
  parNuit: number
  uneLangue: boolean
}

/** Le nom d'une zone : une province ou un pays, sinon une région des Réglages. */
export function useNomZone(): (z: Zone) => string {
  const t = useT()
  return (z) => t(((SOUS_ZONES as readonly string[]).includes(z) ? `zones.${z}` : `regions.${z}`) as CleUi)
}

/** Le filtre en quelques mots, pour la ligne d'un site suivi. */
export function useResumeFiltre(): (f: FiltreAbonnement) => string {
  const nomZone = useNomZone()
  return (f) => [...(f.zones ?? []).map(nomZone), ...(f.langues ?? []).map((l) => l.toUpperCase())].join(', ')
}

function pastille(on: boolean, libelle: string, n: number, clic: () => void) {
  return (
    <button key={libelle} type="button" aria-pressed={on} className={`region${on ? ' on' : ''}`} onClick={clic}>
      {on && <Check size={12} strokeWidth={3} />}
      {libelle} <span className="acc-gris">{n}</span>
    </button>
  )
}

function FenetreFiltre({
  options,
  nom,
  dejaSuivi,
  onFermer,
  onValider,
}: {
  options: OptionsFiltre
  nom: string
  dejaSuivi: boolean
  onFermer: () => void
  onValider: (f: FiltreAbonnement | null) => Promise<void>
}) {
  const t = useT()
  const nomZone = useNomZone()
  const depart = options.filtre ?? options.proposition
  const [zones, setZones] = useState<Zone[]>(depart?.zones ?? [])
  const [langues, setLangues] = useState<Langue[]>(depart?.langues ?? [])
  const [envoi, setEnvoi] = useState(false)
  const bascule = <T,>(liste: T[], v: T) => (liste.includes(v) ? liste.filter((x) => x !== v) : [...liste, v])
  const vide = zones.length === 0 && langues.length === 0
  // Un site traduit (Elia) se suit dans une seule langue : chaque poste y est
  // publié dans chacune (issue #194).
  const incomplet = options.uneLangue ? langues.length !== 1 : options.gros && vide
  return (
    <Modal
      title={t('filtre.titre', { nom })}
      onClose={onFermer}
      footer={
        <>
          <button className="btn-secondary" onClick={onFermer}>{t('common.cancel')}</button>
          <button
            className="btn-primary"
            disabled={envoi || incomplet}
            onClick={async () => {
              setEnvoi(true)
              try {
                await onValider(vide ? null : { zones, langues })
              } finally {
                setEnvoi(false)
              }
            }}
          >
            {dejaSuivi ? t('filtre.enregistrer') : t('filtre.suivre')}
          </button>
        </>
      }
    >
      <p className="field-help">
        {t(options.uneLangue ? 'filtre.aideTraduite' : options.gros ? 'filtre.aide' : 'filtre.aidePetit', { n: options.total })}
      </p>
      {options.zones.length === 0 && options.langues.length === 0 && <p className="field-help">{t('filtre.vide')}</p>}
      {options.zones.length > 0 && (
        <div className="cfg-sous">
          <b>{t('filtre.zones')}</b>
          <div className="regions">
            {options.zones.map((z) => pastille(zones.includes(z.code), nomZone(z.code), z.n, () => setZones(bascule(zones, z.code))))}
          </div>
        </div>
      )}
      {/* Une seule langue sur le site ne filtre rien, sauf si elle est déjà
          choisie : la cacher l'effacerait à l'enregistrement. */}
      {(options.langues.length > 1 || (depart?.langues?.length ?? 0) > 0) && (
        <div className="cfg-sous">
          <b>{t('filtre.langues')}</b>
          <div className="regions">
            {options.langues.map((l) =>
              pastille(langues.includes(l.code), t(`langues.${l.code}` as CleUi), l.n, () =>
                setLangues(options.uneLangue ? [l.code] : bascule(langues, l.code))
              )
            )}
          </div>
        </div>
      )}
      {/* Sans cette phrase, on croit tout lu au premier soir (issue #201). */}
      {options.gros && <p className="field-help">{t('filtre.lecture', { n: options.parNuit })}</p>}
    </Modal>
  )
}

export interface ResultatSuivi {
  relu?: boolean
}

/**
 * `suivre(id, nom)` suit un site et rend la réponse du serveur, ou `null` si
 * la personne a fermé la fenêtre sans choisir. `changer(id, nom)` rouvre la
 * fenêtre sur un site déjà suivi. `fenetre` est à rendre dans la page.
 */
export function useSuivreSite(): {
  suivre: (id: number, nom: string) => Promise<ResultatSuivi | null>
  changer: (id: number, nom: string) => Promise<boolean>
  fenetre: React.ReactNode
} {
  const t = useT()
  const toast = useToast()
  const [ouverte, setOuverte] = useState<{ id: number; nom: string; options: OptionsFiltre; dejaSuivi: boolean } | null>(null)
  const reponse = useRef<((r: ResultatSuivi | null) => void) | null>(null)

  const demander = async (id: number, nom: string, dejaSuivi: boolean): Promise<ResultatSuivi | null> => {
    const options = await get<OptionsFiltre>(`/api/sources/${id}/filtre`)
    return new Promise((resolve) => {
      reponse.current = resolve
      setOuverte({ id, nom, options, dejaSuivi })
    })
  }
  const fermer = (r: ResultatSuivi | null) => {
    reponse.current?.(r)
    reponse.current = null
    setOuverte(null)
  }

  const suivre = async (id: number, nom: string) => {
    try {
      return await post<ResultatSuivi>(`/api/sources/${id}/abonnement`, { abonne: true })
    } catch (e) {
      if (e instanceof ApiError && e.code === 'filtre_requis') return demander(id, nom, false)
      throw e
    }
  }

  const changer = async (id: number, nom: string) => Boolean(await demander(id, nom, true))

  const fenetre = ouverte && (
    <FenetreFiltre
      options={ouverte.options}
      nom={ouverte.nom}
      dejaSuivi={ouverte.dejaSuivi}
      onFermer={() => fermer(null)}
      onValider={async (filtre) => {
        try {
          const r = await post<ResultatSuivi>(`/api/sources/${ouverte.id}/abonnement`, { abonne: true, filtre })
          if (ouverte.dejaSuivi) toast(t('filtre.enregistre'))
          fermer(r ?? {})
        } catch (e: any) {
          toast(e?.message ?? String(e), 'err')
        }
      }}
    />
  )
  return { suivre, changer, fenetre }
}

/** Le filtre d'un site suivi, sur sa ligne : ce qu'il garde, et un clic pour
 *  le changer. */
export function BoutonFiltre({ filtre, onClick }: { filtre: FiltreAbonnement; onClick: () => void }) {
  const t = useT()
  const resume = useResumeFiltre()
  return (
    <button
      type="button"
      className="chip"
      title={t('filtre.changer')}
      aria-label={`${t('filtre.changer')} : ${resume(filtre)}`}
      onClick={(e) => {
        // La ligne entière ouvre la fiche de l'employeur.
        e.stopPropagation()
        onClick()
      }}
    >
      <Filter size={12} /> {resume(filtre)}
    </button>
  )
}
